import MojitoPresentation from 'mojito/presentation';
import { useMemo, useState } from 'react';
import { getCssProperties } from 'mojito/utils';
import { ruleTemplates } from './multi-odds-boost-style';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

const { useCssRule } = MojitoCore.Presentation.Hooks;
const { classUtils } = MojitoCore.Base;
const { Dialog, FlexPane, Text, Button } = MojitoPresentation.Components;
const {
    DateTimeFormatter,
    DateTimeTypes: { DATE_TIME_FORMAT },
} = MojitoPresentation.Utils.DateTime;
const BetslipUtils = MojitoServices.Betslip.utils;

const MultiOddsBoostsDialog = props => {
    const {
        mojitoTools: { config, stringResolver, appContext, style },
        boosts,
        isOpen,
        onClose,
        onApply,
    } = props;
    const defaultCheckedBoost = boosts.length > 0 ? boosts[0].id : null;
    const [selectedBoostId, setSelectedBoostId] = useState(defaultCheckedBoost);

    const onSelectBoost = ({ target }) => {
        setSelectedBoostId(target.value);
    };

    const onApplyBoost = () => {
        onApply(selectedBoostId);
        onClose();
    };

    const renderHeader = () => (
        <Text config={config.headerText} class="ta-MultiOddsBoostsDialogHeader">
            {stringResolver.resolveString('$MULTI_ODDS_BOOSTS_DIALOG.HEADER')}
        </Text>
    );

    return (
        <Dialog
            config={config.dialog}
            isOpen={isOpen}
            headerComponent={renderHeader()}
            onClickOutside={onClose}
            class="ta-MultiOddsBoostsDialog"
            footerComponent={
                <FlexPane config={config.buttonsContainer} class="ta-MultiOddsBoostsDialogButtons">
                    <Button
                        config={config.cancelButton}
                        onClick={onClose}
                        class="ta-MultiOddsBoostsCancelButton"
                    >
                        {stringResolver.resolveString('$MULTI_ODDS_BOOSTS_DIALOG.CANCEL')}
                    </Button>
                    <Button
                        config={config.applyButton}
                        onClick={onApplyBoost}
                        disabled={!selectedBoostId}
                        class="ta-MultiOddsBoostsApplyButton"
                    >
                        {stringResolver.resolveString('$MULTI_ODDS_BOOSTS_DIALOG.APPLY')}
                    </Button>
                </FlexPane>
            }
        >
            <FlexPane config={config.container} class="ta-MultiOddsBoostsContainer">
                <FlexPane config={config.boosts} class="ta-MultiOddsBoostsList">
                    {boosts.map(boostInfo => (
                        <FlexPane
                            config={config.boost}
                            key={boostInfo.id}
                            class="ta-MultiOddsBoostItem"
                        >
                            <label>
                                <FlexPane config={config.titleContainer}>
                                    <Text config={config.title} class="ta-MultiOddsBoostTitle">
                                        {resolveBoostLabel(boostInfo, stringResolver)}
                                    </Text>
                                    <RadioInput
                                        id={boostInfo.id}
                                        selectedBoostId={selectedBoostId}
                                        config={config.radioInput}
                                        customStyle={style}
                                        onSelectBoost={onSelectBoost}
                                    />
                                </FlexPane>
                                <Text config={config.text} class="ta-MultiOddsBoostText">
                                    {boostInfo.text}
                                </Text>
                                {boostInfo.expireDate && (
                                    <Text config={config.expiry} class="ta-MultiOddsBoostExpiry">
                                        {resolveExpiryLabel(
                                            stringResolver,
                                            boostInfo.expireDate,
                                            appContext
                                        )}
                                    </Text>
                                )}
                            </label>
                        </FlexPane>
                    ))}
                </FlexPane>
            </FlexPane>
        </Dialog>
    );
};

const resolveBoostLabel = (boostInfo, stringResolver) => {
    const { title, value, contributionSource, oddsBoostSubtype } = boostInfo;
    const labelPrefix = BetslipUtils.resolveBoostLabel(contributionSource, oddsBoostSubtype, {
        PROFIT: '$MULTI_ODDS_BOOSTS_DIALOG.PERCENT',
        RETURN: '$MULTI_ODDS_BOOSTS_DIALOG.PERCENT',
        FIXED: '$MULTI_ODDS_BOOSTS_DIALOG.VALUE',
    });
    const label = value ? stringResolver.resolveAndFormatString(labelPrefix, value) : '';
    return [label, title].filter(Boolean).join(' ');
};

const resolveExpiryLabel = (stringResolver, expireDate, appContext) => {
    const formattedDate = DateTimeFormatter.formatDateTime(
        expireDate,
        DATE_TIME_FORMAT.NUMERIC_DATE_TIME,
        appContext.userSettings(),
        appContext.systemSettings().language
    );
    const expiryTitle = stringResolver.resolveString('$MULTI_ODDS_BOOSTS_DIALOG.EXPIRY');
    return [expiryTitle, formattedDate].filter(Boolean).join(' ');
};

MultiOddsBoostsDialog.getStyle = (config, mode, merge) => {
    const { radioInput } = config;
    const base = merge(
        {
            margin: 0,
            appearance: 'none',
        },
        radioInput.style.base
    );

    return {
        radioInputBase: base,
        radioInputChecked: merge(base, radioInput.style.checked),
    };
};

function getCssVariables(config, classUUID) {
    const { checked } = config.style;
    const uniqueClassSegment = classUUID ? `.${classUUID}` : '';
    return {
        '%class%': `.${config.class}${uniqueClassSegment}`,
        '%checked%': getCssProperties(checked),
    };
}

const RadioInput = props => {
    const { onSelectBoost, id, config, selectedBoostId, customStyle } = props;

    const classUUID = useMemo(() => classUtils.createClassUUID(config.class), [config.class]);
    const cssVars = useMemo(() => getCssVariables(config, classUUID), [config, classUUID]);
    useCssRule(classUUID, cssVars, ruleTemplates);

    return (
        <input
            className={classUtils.classNames('ta-RadioInput', config.class, classUUID)}
            style={
                id === selectedBoostId ? customStyle.radioInputChecked : customStyle.radioInputBase
            }
            type="radio"
            checked={id === selectedBoostId}
            name="boost"
            value={id}
            onChange={onSelectBoost}
        />
    );
};

export default MultiOddsBoostsDialog;
